import React, { useCallback, useEffect, useState } from "react";
import "./PaymentSuccessful.css";
import { useNavigate } from "react-router-dom";
import CheckoutFooter from "../ComponentsCheckoutPage/Components/CheckoutFooter/CheckoutFooter";
import { CircularProgress } from "@mui/material";
import { NFOLogo, TickIcon } from "../../Assets/svgs";
import { formatDateTime } from "../../Utils/TimeConverter";
import {
  getStudentPostPayment,
  verifyNFOTransaction,
  verifyNFOTransactionRazorpay,
} from "../../Utils/CheckoutAPIs/CheckoutAPIs";
import ReactGA from "react-ga4";
import { trackData, getCookie } from "../../Utils/attribution/CheckoutAttribution";

const NFH_DEVICE_ID_KEY = "nfh_device_id";

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(NFH_DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(NFH_DEVICE_ID_KEY, id);
  }
  return id;
}

const PaymentSuccessful = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const orderId = urlParams.get("order_id");
  const schoolCode = urlParams.get("scl");

  const isInternationalTransaction = sessionStorage.getItem(
    "isInternationalTransaction"
  );
  let isInternational = false;
  try {
    isInternational = JSON.parse(isInternationalTransaction ?? "false");
  } catch (error) {
    console.error("Error parsing isInternationalTransaction:", error);
    isInternational = false;
  }
  const examDate = sessionStorage.getItem("examDate");
  const convertedExamDate = examDate && formatDateTime(examDate?.toString());
  const transactionAmount = sessionStorage.getItem("transactionAmount");

  const [alertToggle, setAlertToggle] = useState({
    alertMessage: "",
    isVisible: false,
  });
  const [studentUsername, setStudentUsername] = useState("");
  const [stadiaReady, setStadiaReady] = useState(false);

  const REFER_MSG =
    "Referral link copied to clipboard. Share it with your friends";

  const [pageLoading, setLoading] = useState(true);
  useEffect(() => {
    trackData("5678_registration_successful");
    ReactGA.event({
      category: "Registration",
      action: "5678_registration_successful",
      label: "Payment_Success_Page",
    });
  }, []);

  const copyReferral = async () => {
    const referralLink = `Hi!\n\nI'm excited to share that I'm participating in India's largest financial literacy\ncompetition—National Finance Olympiad 2026-27! 🎉\n\nThis Olympiad is a unique opportunity for students like us to gain essential financial\nskills, from investing, financial planning to understanding banking and more. It's a\nchance to learn, grow, and prepare for a financially smart future!\n\nI'd love for you to join me in this journey! Compete with me and let's see\nwho can master the world of finance. 💸\n\nClick here to learn more and register now: https://nationalfinanceolympiad.com/nfo-mobile-5678`;
    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setAlertToggle({ alertMessage: REFER_MSG, isVisible: true });
        setTimeout(() => {
          setAlertToggle({ alertMessage: "", isVisible: false });
        }, 10000);
      })
      .catch((err) => {
        console.error("Failed to copy the text to clipboard: ", err);
      });
    if (navigator.share) {
      await navigator
        .share({
          title: "National Finance Olympiad",
          text: "Hi!\n\nI'm excited to share that I'm participating in India's largest financial literacy\ncompetition—National Finance Olympiad 2026-27! 🎉\n\nThis Olympiad is a unique opportunity for students like us to gain essential financial\nskills, from investing, financial planning to understanding banking and more. It's a\nchance to learn, grow, and prepare for a financially smart future!\n\nI'd love for you to join me in this journey! Compete with me and let's see\nwho can master the world of finance. 💸\n\nClick here to learn more and register now: ",
          url: "https://nationalfinanceolympiad.com/nfo-mobile-5678",
        })
        .then(() => console.log("Successful share"))
        .catch((error) => console.log("Error sharing", error));
    }
  };

  const copyUserName = (userName: string) => {
    navigator.clipboard
      .writeText(userName)
      .then(() => {
        setAlertToggle({
          alertMessage: "Roll Number copied to clipboard.",
          isVisible: true,
        });
        setTimeout(() => {
          setAlertToggle({ alertMessage: "", isVisible: false });
        }, 10000);
      })
      .catch((err) => {
        console.error("Failed to copy the text to clipboard: ", err);
      });
  };

  const openNFOStudentHub = () => {
    window.open(
      `https://nfostadia.nationalfinanceolympiad.com/?&userName=${studentUsername}`,
      "_blank"
    );
  };

  const verifyPaymentStripe = useCallback(async (orderId: string) => {
    try {
      // Create a Checkout Session
      const response = await fetch(
        "https://streak-payments.el.r.appspot.com/nfo/stripe-metadata",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId: orderId,
          }),
        }
      );
      const data = await response.json();

      if (
        data.data.message ===
        "Metadata retrieved, student onboarded, and invoice sent successfully."
      ) {
        console.log("Payment Successful");
        setLoading(false);
      } else {
        navigate(`/CheckoutPage/?&scl=${schoolCode}`);
      }

      console.log(data.id);
      return data.clientSecret; // Assuming `data.id` is the client secret you need
    } catch (error) {
      navigate(`/CheckoutPage/?&scl=${schoolCode}`);
      // Handle the error accordingly
      return null; // Or throw error depending on your error handling strategy
    }
  }, []);

  const ir = sessionStorage.getItem("individual_registration");

  const verifyPaymentBilldesk = async (orderId: string) => {
    try {
      const errorStatuses = [
        "payment_processing_error",
        "user_cancelled_error",
        "acquirer_processing_error",
        "risk_validation_error",
      ];

      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyNFOTransaction(orderId);
          console.log("This is new verify response after retry =", newResponse);
          //@ts-ignore
          const newPaymentStatus = newResponse.data.data.paymentStatus;
          if (newPaymentStatus === "success") {
            console.log("Payment Successful");
            return true;
          } else if (newPaymentStatus === "retry") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
          } else if (errorStatuses.includes(newPaymentStatus)) {
            console.log("Payment failed");
            return false;
          } else {
            return false;
          }
        }
        return false;
      };

      const response = await verifyNFOTransaction(orderId);
      console.log("This is the verify response", response);
      //@ts-ignore
      const paymentStatus = response.data.data.paymentStatus;
      if (paymentStatus === "success") {
        console.log("Payment Successful");
        return true;
      } else if (paymentStatus === "retry") {
        console.log("Retry payment");
        return await retryPayment(3);
      } else if (errorStatuses.includes(paymentStatus)) {
        console.log("Payment failed");
        return false;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const verifyPayment = async (orderId: string) => {
    const paymentGateway = sessionStorage.getItem("pg");

    try {
      if (paymentGateway === "razorpay") {
        verifyNFOTransactionRazorpay(orderId)
          .then((response) => {
            if (response.status_code === 200) {
              if (
                response &&
                response.data &&
                response.data.status === "paid"
              ) {
                const isStadia = sessionStorage.getItem("stadiaAutoLogin") === "true";
                const tryFetchWithDevice = (deviceId: string, isRetry = false): Promise<void> => {
                  return getStudentPostPayment(orderId, {
                    newhub: true,
                    gettoken: true,
                    device_id: deviceId,
                  }).then((res) => {
                    if (res.status === "Success" && res.data?.studentUsername) {
                      setStudentUsername(res.data.studentUsername);
                      if (res.data.token) sessionStorage.setItem("newhubToken", res.data.token);
                      if (res.data.refresh_token) sessionStorage.setItem("newhubRefreshToken", res.data.refresh_token as string);
                      setStadiaReady(true);
                    } else if (!isRetry && res.message?.includes("duplicate key")) {
                      const newId = crypto.randomUUID();
                      localStorage.setItem(NFH_DEVICE_ID_KEY, newId);
                      return tryFetchWithDevice(newId, true);
                    } else {
                      if (isStadia) setStadiaReady(true);
                    }
                  }).catch((err) => {
                    if (!isRetry && err?.response?.data?.message?.includes("duplicate key")) {
                      const newId = crypto.randomUUID();
                      localStorage.setItem(NFH_DEVICE_ID_KEY, newId);
                      return tryFetchWithDevice(newId, true);
                    }
                    if (isStadia) setStadiaReady(true);
                  });
                };
                tryFetchWithDevice(getOrCreateDeviceId());
              } else {
                navigate(`/CheckoutPage/?&scl=${schoolCode}`);
              }
              setLoading(false);
            } else {
              navigate(`/CheckoutPage/?&scl=${schoolCode}`);
            }
          })
          .catch((error) => {
            navigate(`/CheckoutPage/?&scl=${schoolCode}`);
          });
      } else {
        const response = await verifyPaymentBilldesk(orderId);
        if (response) {
          const deviceId = localStorage.getItem(NFH_DEVICE_ID_KEY) ?? undefined;
          const isStadia = sessionStorage.getItem("stadiaAutoLogin") === "true";
          getStudentPostPayment(
            orderId,
            isStadia && deviceId
              ? { newhub: true, gettoken: true, device_id: deviceId }
              : undefined
          )
            .then((res) => {
              if (res.status === "Success" && res.data?.studentUsername) {
                setStudentUsername(res.data.studentUsername);
                if (isStadia) {
                  if (res.data.token) sessionStorage.setItem("newhubToken", res.data.token);
                  if (res.data.refresh_token) sessionStorage.setItem("newhubRefreshToken", res.data.refresh_token as string);
                  setStadiaReady(true);
                }
              }
            })
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
        } else {
          navigate(`/CheckoutPage/?&scl=${schoolCode}`);
        }
      }
    } catch (err) {
      console.log(err);
      navigate(`/CheckoutPage/?&scl=${schoolCode}`);
    }
  };

  useEffect(() => {
    setLoading(true);

    if (orderId) {
      // Only verify payment if order_id is present
      if (isInternational) {
        verifyPaymentStripe(orderId);
      } else {
        verifyPayment(orderId);
      }
    } else {
      // If no order_id, just show success state without API calls
      setLoading(false);
    }
  }, []);

  const navigate = useNavigate();
  return pageLoading ? (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
      }}
    >
      <CircularProgress />
    </div>
  ) : (
    <div className="payment-successful-page">
      <img src={NFOLogo} className="nfo-logo" alt="NFO Logo" />
      <div className="line"></div>
      <div className="payment-success-container-parent">
        <div className="payment-success-container">
          <img src={TickIcon} className="tick-icon" alt="Success Tick" />
          <div className="successful-title">
            Thank you for registering for the National Finance Olympiad 2026-27.
          </div>
          <div className="successful-subtitle">
            {orderId
              ? studentUsername !== ""
                ? ""
                : `We will be sharing your child's roll number and other details to
              your registered E-mail ID and mobile number shortly.`
              : `Your child's roll number and other details will be shared to your registered email ID and mobile number upon successful delivery.`}
          </div>
        </div>
        <div className="payment-success-secondary-container">
          {ir === "true" && orderId && (
            <div className="payment-success-details-section">
              {/* <div>
                <div className="date-of-exam">Date of Exam</div>
                <div className="date">
                  {convertedExamDate?.split(",")[0]},<br></br>
                  {convertedExamDate?.split(",")[1]} IST
                </div>
              </div> */}

              <div>
                <div className="total">Total</div>
                <div className="amount">
                  ₹{transactionAmount && transactionAmount.toString()}
                </div>
              </div>
            </div>
          )}

          <div className="successful-subtitle">
            {orderId &&
              studentUsername !== "" &&
              "Your NFO 2026-27 roll number is"}
          </div>

          {orderId && studentUsername !== "" && (
            <div
              onClick={() => copyUserName(studentUsername)}
              style={{
                marginTop: "20px",
                backgroundColor: "rgba(237, 247, 224, 1)",
                padding: "10px",
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                borderRadius: "8px",
                fontSize: "1.5rem",
                fontWeight: "900",
                letterSpacing: "0.1rem",
                color: "rgba(67, 137, 34, 1)",
                cursor: "pointer",
              }}
            >
              {studentUsername}
            </div>
          )}

         

          {sessionStorage.getItem("stadiaAutoLogin") === "true" && (
            stadiaReady ? (
              <div
                onClick={() => {
                  const token = sessionStorage.getItem("newhubToken") ?? "";
                  const refreshToken = sessionStorage.getItem("newhubRefreshToken") ?? "";
                  const phone = sessionStorage.getItem("mobileNumber") ?? "";

                  // Forward UTM params and session_id so stadia attribution is connected
                  const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
                  const utmParams: Record<string, string> = {};
                  utmKeys.forEach((key) => {
                    const val = sessionStorage.getItem(key);
                    if (val) utmParams[key] = val;
                  });
                  const sessionId = getCookie("session_id");

                  const params = new URLSearchParams({
                    source: "nfo-checkout",
                    ...(token ? { token } : {}),
                    ...(refreshToken ? { refreshToken } : {}),
                    ...(phone ? { phone } : {}),
                    ...(studentUsername ? { studentUsername } : {}),
                    ...utmParams,
                    ...(sessionId ? { session_id: sessionId } : {}),
                  });
                  window.location.href = `https://stadia.nationalfinanceolympiad.com/auth/callback?${params.toString()}`;
                }}
                className="share-button"
                style={{ marginTop: "12px", background: "#2d5fa6" }}
              >
                <div className="button-text">Access Study Materials</div>
              </div>
            ) : (
              <div style={{ marginTop: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", color: "#666", fontSize: "14px" }}>
                <CircularProgress size={16} />
                Setting up your dashboard…
              </div>
            )
          )}

          {!orderId && (
            <div
              className="successful-subtitle"
              style={{ marginTop: "20px", color: "#666" }}
            >
              Your registration details have been saved. You will receive a
              confirmation email shortly.
            </div>
          )}

          <div
            onClick={copyReferral}
            style={{
              fontSize: "1rem",
              color: "rgba(36, 109, 194, 1)",
              fontFamily: "OggTextRegular",
              cursor: "pointer",
              marginTop: "20px",
              alignSelf: "center",
              textAlign: "center",
            }}
          >
            Share NFO with your friends & family
          </div>

          {alertToggle.isVisible && (
            <div
              className={`referral-toggle ${
                alertToggle.isVisible ? "fade-in" : "fade-out"
              } `}
            >
              &#9432; {alertToggle.alertMessage}
            </div>
          )}
        </div>
      </div>

      <CheckoutFooter />
    </div>
  );
};

export default PaymentSuccessful;
